import { zoneAnalysisConfigSchema } from 'features/libra/stepComponents/zoneAnalysis/zoneAnalysisApi'
import type { LibraWorkflowStep } from 'features/libra/workflow/workflowStepsApi'

export type ZoneCoverageBinding =
  | Readonly<{
      zoneFieldName: string
      confidenceFieldName?: string
      error?: never
    }>
  | Readonly<{
      zoneFieldName: string
      error: string
      confidenceFieldName?: never
    }>

/** Resolve each root ZoneAnalysis collection to one coverage binding or error. */
export function resolveZoneCoverageBindings(
  steps: ReadonlyArray<LibraWorkflowStep>
): ReadonlyArray<ZoneCoverageBinding> {
  const bindingSchema = zoneAnalysisConfigSchema.pick({
    zoneFieldName: true,
    confidenceFieldName: true,
  })
  const bindings = new Map<string, ZoneCoverageBinding>()

  for (const step of steps) {
    if (step.component !== 'ZoneAnalysis' || step.requiresComponentScope)
      continue

    const parsed = bindingSchema.safeParse(step.config)
    if (parsed.success) {
      const { zoneFieldName, confidenceFieldName } = parsed.data
      const normalizedConfidenceFieldName = confidenceFieldName || undefined
      const previous = bindings.get(zoneFieldName)
      if (!previous) {
        bindings.set(zoneFieldName, {
          zoneFieldName,
          ...(normalizedConfidenceFieldName
            ? { confidenceFieldName: normalizedConfidenceFieldName }
            : {}),
        })
      } else if (previous.error) {
        continue
      } else if (
        previous.confidenceFieldName !== normalizedConfidenceFieldName
      ) {
        bindings.set(zoneFieldName, {
          zoneFieldName,
          error: `Multiple root ZoneAnalysis steps configure ${zoneFieldName}`,
        })
      }
      continue
    }

    const zoneFieldName =
      zoneAnalysisConfigSchema.shape.zoneFieldName.safeParse(
        step.config.zoneFieldName
      )
    if (!zoneFieldName.success) continue

    const previous = bindings.get(zoneFieldName.data)
    if (previous?.error) continue
    bindings.set(zoneFieldName.data, {
      zoneFieldName: zoneFieldName.data,
      error: 'Invalid ZoneAnalysis coverage binding config',
    })
  }

  return [...bindings.values()]
}
